import React, { useState, useEffect } from 'react';
import { Button, Modal, Space, Tag, Typography, Spin, Divider, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, HistoryOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { MedicalHistory, historyTypesOptions } from '../../../../../../types/healthcare/MedicalHistoryTypes';
import { fetchMedicalHistories, updateMedicalHistory, deleteMedicalHistory, resolveMedicalHistory } from '../../../../../../api/healthcare/PatientApi';
import CreateMedicalHistoryModal from '../../../../patient/modalComponents/submodals/CreateMedicalHistoryModal';
import { useNotifications } from '../../../../../../context/NotificationContext';
import dayjs from 'dayjs';
import type { UserUuid } from '../../../../../../types/identity';

const { Text } = Typography;

interface Props {
  taskConfig: Record<string, any>;
  patientId: UserUuid;
  onChange: (payload: Record<string, any>, isValid: boolean) => void;
}

const SEVERITY_COLOR: Record<string, string> = {
  mild: 'green',
  moderate: 'orange',
  severe: 'red',
  life_threatening: 'red',
};

const DocumentMedicalHistoryTaskForm: React.FC<Props> = ({ patientId, onChange }) => {
  const [entries, setEntries] = useState<Partial<MedicalHistory>[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Partial<MedicalHistory>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // When non-null, the modal is editing an existing saved record (not staging a new entry)
  const [editingExistingRecord, setEditingExistingRecord] = useState<MedicalHistory | null>(null);
  const [existingHistories, setExistingHistories] = useState<MedicalHistory[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const { showSuccess, showError } = useNotifications();

  const loadExisting = (currentEntries: Partial<MedicalHistory>[] = entries) => {
    fetchMedicalHistories(patientId)
      .then((histories) => {
        setExistingHistories(histories);
        onChange({ histories: currentEntries }, histories.length > 0 || currentEntries.length > 0);
      })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  };

  useEffect(() => {
    setLoadingExisting(true);
    loadExisting([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const openAddModal = () => {
    setEditingIndex(null);
    setEditingExistingRecord(null);
    setCurrentRecord({ patient_id: patientId, history_type: '', description: '', details: {} });
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setEditingIndex(index);
    setEditingExistingRecord(null);
    setCurrentRecord({ ...entries[index] });
    setModalOpen(true);
  };

  /** Open the modal pre-filled for editing a saved existing record (not a staged new entry). */
  const openEditExistingModal = (record: MedicalHistory) => {
    setEditingIndex(null);
    setEditingExistingRecord(record);
    setCurrentRecord({ ...record });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    if (!currentRecord.history_type || !currentRecord.description) return;

    // Editing an already-saved existing record — persist immediately.
    if (editingExistingRecord !== null) {
      try {
        const payload = {
          ...currentRecord,
          details: JSON.stringify(currentRecord.details || {}),
        } as unknown as Partial<MedicalHistory>;
        await updateMedicalHistory(editingExistingRecord.id, payload);
        showSuccess('Record updated successfully');
        setModalOpen(false);
        setEditingExistingRecord(null);
        loadExisting(entries);
      } catch {
        showError('Failed to update record');
      }
      return;
    }

    // Staging a new entry (or editing a staged-but-not-yet-saved entry).
    const newEntries =
      editingIndex !== null
        ? entries.map((e, i) => (i === editingIndex ? currentRecord : e))
        : [...entries, currentRecord];
    setEntries(newEntries);
    setModalOpen(false);
    onChange({ histories: newEntries }, existingHistories.length > 0 || newEntries.length > 0);
  };

  const handleRemove = (index: number) => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    onChange({ histories: newEntries }, existingHistories.length > 0 || newEntries.length > 0);
  };

  /** Resolve an existing saved record. */
  const handleResolveExisting = (record: MedicalHistory) => {
    Modal.confirm({
      title: 'Mark as Resolved',
      content: `Mark "${record.description}" as resolved?`,
      okText: 'Resolve',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await resolveMedicalHistory(record.id);
          showSuccess('Record marked as resolved');
          loadExisting(entries);
        } catch {
          showError('Failed to resolve record');
        }
      },
    });
  };

  /** Delete an existing saved record. */
  const handleDeleteExisting = (record: MedicalHistory) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this medical history record?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteMedicalHistory(record.id);
          showSuccess('Record deleted successfully');
          loadExisting(entries);
        } catch {
          showError('Failed to delete record');
        }
      },
    });
  };

  const getTypeLabel = (type: string) =>
    historyTypesOptions.find((o) => o.value === type)?.label ?? type;

  const isModalValid = !!currentRecord.history_type && !!currentRecord.description;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={6}>

      {/* Existing histories */}
      <div style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--mg-border)', background: 'var(--mg-bg-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <HistoryOutlined style={{ color: 'var(--mg-text-secondary)', fontSize: 11 }} />
          <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--mg-text)' }}>Existing Medical History</Text>
          {!loadingExisting && (
            <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>({existingHistories.length})</Text>
          )}
        </div>
        {loadingExisting ? (
          <Spin size="small" />
        ) : existingHistories.length === 0 ? (
          <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)' }}>No existing medical history on file.</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {existingHistories.map((h) => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--mg-border)', background: h.is_active ? 'var(--mg-bg)' : 'var(--mg-bg-subtle)', opacity: h.is_active ? 1 : 0.6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <Tag color="blue" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{getTypeLabel(h.history_type)}</Tag>
                    {h.severity && (
                      <Tag color={SEVERITY_COLOR[h.severity.toLowerCase()] ?? 'default'} style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{h.severity}</Tag>
                    )}
                    {!h.is_active && <Tag style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>Resolved</Tag>}
                  </div>
                  <Text style={{ fontSize: 11, display: 'block', marginTop: 2, lineHeight: 1.4 }}>{h.description}</Text>
                  {h.date_identified && (
                    <Text style={{ fontSize: 10, color: 'var(--mg-text-secondary)' }}>
                      {dayjs(h.date_identified).format('DD MMM YYYY')}
                    </Text>
                  )}
                </div>
                <Space size={0} style={{ flexShrink: 0 }}>
                  <Tooltip title="Edit">
                    <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditExistingModal(h)} />
                  </Tooltip>
                  {h.is_active && (
                    <Tooltip title="Mark resolved">
                      <Button type="text" size="small" icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />} onClick={() => handleResolveExisting(h)} />
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteExisting(h)} />
                  </Tooltip>
                </Space>
              </div>
            ))}
          </div>
        )}
      </div>

      <Divider style={{ margin: '2px 0' }} />

      {/* New histories */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 11, fontWeight: 600, color: 'var(--mg-text)' }}>New Histories to Document</Text>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAddModal}>
          Add
        </Button>
      </div>

      {entries.length === 0 ? (
        <Text style={{ fontSize: 11, color: 'var(--mg-text-secondary)', display: 'block', padding: '4px 0' }}>
          No new histories — existing records will be submitted.
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {entries.map((entry, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--mg-border)', background: 'var(--mg-bg)' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                <Tag color="blue" style={{ margin: 0, fontSize: 10, padding: '0 4px' }}>{getTypeLabel(entry.history_type ?? '')}</Tag>
                <Text style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.description}</Text>
              </div>
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(index)} />
              <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(index)} />
            </div>
          ))}
        </div>
      )}

      <Modal
        title={editingExistingRecord !== null || editingIndex !== null ? 'Edit Medical History' : 'Add Medical History'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingExistingRecord(null); }}
        onOk={handleModalOk}
        okText={editingExistingRecord !== null || editingIndex !== null ? 'Update' : 'Add'}
        okButtonProps={{ disabled: !isModalValid }}
        width="85vw"
        destroyOnClose
      >
        <CreateMedicalHistoryModal
          currentRecord={currentRecord}
          isEditing={editingExistingRecord !== null || editingIndex !== null}
          setCurrentRecord={setCurrentRecord as React.Dispatch<React.SetStateAction<Partial<MedicalHistory | null>>>}
        />
      </Modal>
    </Space>
  );
};

export default DocumentMedicalHistoryTaskForm;
